import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bot, Search, Megaphone, FileText, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import { TextReveal } from "@/components/ui/TextReveal";

import { ParallaxCard } from "@/components/ui/ParallaxCard";
import { AnimatedIcon } from "@/components/ui/AnimatedIcon";
import { AnimatedBackground, GradientBlob } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { SEO } from "@/components/SEO";

const services = [
  {
    title: "GEO (Generative Engine Optimization)",
    description: "Get discovered by ChatGPT, Claude, and Perplexity. The new search frontier.",
    icon: Search,
    href: "/services/geo",
    animatedIcon: "search" as const,
    features: [
      "AI search visibility optimization",
      "Content structuring for LLMs",
      "Citation building strategy",
      "Monthly AI rankings reports",
    ],
  },
  {
    title: "AI-Powered Paid Ads",
    description: "Smarter campaigns on Google, Meta, and LinkedIn with AI optimization.",
    icon: Megaphone,
    href: "/services/ai-ads",
    animatedIcon: "megaphone" as const,
    features: [
      "AI-generated ad creatives",
      "Predictive audience targeting",
      "Automated bid optimization",
      "Cross-platform analytics",
    ],
  },
  {
    title: "AI Content & SEO",
    description: "Scale content production 10x while maintaining quality and rankings.",
    icon: FileText,
    href: "/services/ai-content",
    animatedIcon: "file" as const,
    features: [
      "AI-assisted content creation",
      "SEO optimization at scale",
      "Content performance tracking",
      "Topic cluster strategy",
    ],
  },
  {
    title: "AI Agents & Automation",
    description: "Custom chatbots, lead qualification, and workflow automation.",
    icon: Bot,
    href: "/services/ai-agents",
    animatedIcon: "bot" as const,
    features: [
      "Custom AI chatbots",
      "Lead qualification automation",
      "CRM integrations",
      "24/7 customer support",
    ],
  },
];

export default function Services() {
  return (
    <Layout>
      <SEO 
        title="AI Marketing Services"
        description="Four pillars of AI-powered marketing: GEO, AI-Powered Ads, AI Content & SEO, and AI Agents. Each service starts at $2,000/month."
        canonical="https://thecloudseo.com/services"
      />
      <ScrollProgress />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <AnimatedBackground variant="mesh" />
        <GradientBlob className="absolute -top-20 -right-20" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.span 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-sm font-medium mb-8"
            >
              Our Services
            </motion.span>
            <TextReveal>
              <h1 className="heading-hero mb-6">
                AI-Powered Marketing
                <br />
                <span className="text-muted-foreground">Services</span>
              </h1>
            </TextReveal>
            <TextReveal delay={0.1}>
              <p className="text-xl text-muted-foreground">
                Four pillars of AI-powered marketing designed for B2B growth. 
                Each service starts at $2,000/month.
              </p>
            </TextReveal>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing relative">
        <AnimatedBackground variant="dots" />
        
        <div className="container-wide relative z-10">
          <div className="space-y-32">
            {services.map((service, index) => (
              <AnimatedSection
                key={service.title}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <motion.div 
                  className={index % 2 === 1 ? "lg:order-2" : ""}
                  initial={{ opacity: 0, x: index % 2 === 1 ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <motion.div 
                    className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-6"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <service.icon className="w-8 h-8" />
                  </motion.div>
                  <h2 className="heading-subsection mb-4">{service.title}</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <motion.li 
                        key={feature} 
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + featureIndex * 0.1 }}
                      >
                        <motion.div 
                          className="w-5 h-5 rounded-full bg-primary flex items-center justify-center"
                          whileHover={{ scale: 1.2 }}
                        >
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </motion.div>
                        <span>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                  <Link to={service.href}>
                    <Button className="rounded-full px-6 hover-glow">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
                
                <ParallaxCard 
                  className={`${index % 2 === 1 ? "lg:order-1" : ""} rounded-3xl overflow-hidden bg-secondary/30 border border-border/50`}
                  intensity={5}
                >
                  <div className="relative aspect-video flex items-center justify-center p-8">
                    <AnimatedIcon 
                      icon={service.animatedIcon}
                      size="lg"
                      className="w-full h-full"
                    />
                  </div>
                </ParallaxCard>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-10"
          animate={{ 
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">
                Not Sure Where to Start?
              </h2>
            </TextReveal>
            <p className="text-xl text-primary-foreground/70 max-w-2xl mx-auto mb-10">
              Book a free strategy call and we'll help you identify the best 
              AI marketing services for your business.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="rounded-full px-10 text-base h-14 bg-primary-foreground text-primary hover:bg-primary-foreground/90 btn-magnetic"
              >
                Book a Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
